import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import cloud from 'd3-cloud';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';

type Theme = {
  text: string;
  value: number;
  sentiment: number;
  emotions: {
    Happy: number;
    Angry: number;
    Surprise: number;
    Sad: number;
    Fear: number;
  };
  keywords: string[];
  related: string[];
  diffType?: string;
  [key: string]: any;
};

const getSentimentColor = (score: number) => {
  // Red for negative, white for neutral, green for positive
  if (score <= -0.5) return '#e74c3c';       // Strong negative
  else if (score < 0) return '#e57373';      // Mild negative
  else if (score === 0) return '#95a5a6';    // Neutral
  else if (score < 0.5) return '#81c784';    // Mild positive
  else return '#2ecc71';                     // Strong positive
};

const getEmotionIcon = (emotions: any) => {
  // Find the dominant emotion
  let dominant = 'Neutral';
  let maxScore = 0;
  
  if (!emotions) return '😐';
  
  for (const [emotion, score] of Object.entries(emotions)) {
    if ((score as number) > maxScore) {
      maxScore = score as number;
      dominant = emotion;
    }
  }
  
  // Return appropriate emoji
  switch (dominant) {
    case 'Happy': return '😊';
    case 'Angry': return '😡';
    case 'Surprise': return '😲';
    case 'Sad': return '😔';
    case 'Fear': return '😨';
    default: return '😐';
  }
};

const SingleWordCloud: React.FC<{ 
  themes: Theme[]; 
  title: string; 
  colorByDiffType?: boolean;
  colorBySentiment?: boolean;
}> = ({ themes, title, colorByDiffType, colorBySentiment }) => {
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    if (!themes.length) return;
    const width = 450;
    const height = 450;
    const margin = 40;
    const svg = d3.select(svgRef.current);
    svg.selectAll("*").remove();

    const sizeScale = (value: number) => Math.log(value + 20) * 6;

    // Modern color palettes
    const preShowColors = [
      '#3498db', '#2980b9', '#1abc9c', '#16a085', '#2ecc71', 
      '#27ae60', '#8e44ad', '#9b59b6'
    ];
    
    const postShowColors = [
      '#e74c3c', '#c0392b', '#d35400', '#e67e22', '#f39c12',
      '#f1c40f', '#95a5a6', '#7f8c8d', '#f1948a', '#fab1a0'
    ];

    // Comparison cloud colors
    const comparisonColors = {
      common: "#27ae60",
      uniquePre: "#e74c3c",
      uniquePost: "#3498db"
    };

    const layout = cloud()
      .size([width - margin * 2, height - margin * 2])
      .words(themes.map(d => ({ ...d, size: sizeScale(d.value) })))
      .padding(3)
      .rotate(() => ~~(Math.random() * 2) * 90)
      .font("Impact")
      .fontSize(d => d.size)
      .on("end", draw);

    layout.start();

    function draw(words: any[]) {
      const g = svg.append("g")
        .attr("transform", `translate(${width / 2},${height / 2})`);
      
      g.selectAll("text")
        .data(words)
        .enter().append("text")
        .style("font-size", d => `${d.size}px`)
        .style("font-family", "Impact")
        .style("fill", (d, i) => {
          if (colorBySentiment && d.sentiment !== undefined) {
            // Color by sentiment if available
            return getSentimentColor(d.sentiment);
          } else if (colorByDiffType) {
            // For comparison cloud
            if (d.diffType === "common") return comparisonColors.common;
            if (d.diffType === "unique-pre") return comparisonColors.uniquePre;
            if (d.diffType === "unique-post") return comparisonColors.uniquePost;
          } else if (title.includes("Pre-Show") || title === "") {
            // Pre-show cloud - blue palette
            return preShowColors[i % preShowColors.length];
          } else {
            // Post-show cloud - red/orange palette
            return postShowColors[i % postShowColors.length];
          }
        })
        .style("opacity", d => {
          return 0.7 + (d.size / 100 * 0.3); // Range from 0.7 to 1.0
        })
        .attr("text-anchor", "middle")
        .attr("transform", d => `translate(${d.x},${d.y}) rotate(${d.rotate})`)
        .text(d => d.text)
        .each(function(this: SVGTextElement, d) {
          // Create enhanced tooltip content
          const emotionIcon = d.emotions ? getEmotionIcon(d.emotions) : '';
          const sentimentValue = d.sentiment !== undefined ? 
            d.sentiment.toFixed(2) : 'N/A';
          const sentimentLabel = d.sentiment > 0.2 ? 'Positive' : 
                                 d.sentiment < -0.2 ? 'Negative' : 'Neutral';
          
          const keywordsStr = d.keywords && d.keywords.length ? 
            `<br/><em>Keywords:</em> ${d.keywords.join(", ")}` : "";
          
          const relatedStr = d.related && d.related.length ? 
            `<br/><em>Related:</em> ${d.related.join(", ")}` : "";
          
          let emotionStr = "";
          if (d.emotions) {
            const topEmotions = Object.entries(d.emotions)
              .sort((a, b) => (b[1] as number) - (a[1] as number))
              .slice(0, 2)
              .map(([emotion, score]) => `${emotion}: ${(Number(score) * 100).toFixed(0)}%`);
            
            if (topEmotions.length) {
              emotionStr = `<br/><em>Emotions:</em> ${topEmotions.join(", ")}`;
            }
          }
          
          const tooltipContent = `
            <div style="text-align:center;margin-bottom:5px;font-weight:bold;font-size:16px;">
              ${d.text} ${emotionIcon}
            </div>
            <div>
              Count: ${d.value}<br/>
              Sentiment: ${sentimentLabel} (${sentimentValue})
              ${emotionStr}
              ${keywordsStr}
              ${relatedStr}
            </div>
          `;
          
          tippy(this, {
            content: tooltipContent,
            allowHTML: true,
            animation: 'scale',
            theme: 'light-border',
            maxWidth: 300
          });
        });
    }
  }, [themes, colorByDiffType, colorBySentiment, title]);

  return (
    <div style={{ flex: 1, textAlign: 'center' }}>
      <h2>{title}</h2>
      <svg ref={svgRef} width={400} height={400}></svg>
    </div>
  );
};

const EnhancedWordCloudVis = () => {
  const [preThemes, setPreThemes] = useState<Theme[]>([]);
  const [postThemes, setPostThemes] = useState<Theme[]>([]);
  const [compThemes, setCompThemes] = useState<Theme[]>([]);
  const [loading, setLoading] = useState(true);
  const [viewMode, setViewMode] = useState<'comparison' | 'sentiment'>('comparison');

  useEffect(() => {
    Promise.all([
      fetch('/wordcloud_themes_pre_improved.json').then(res => res.json()),
      fetch('/wordcloud_themes_post_improved.json').then(res => res.json())
    ])
      .then(([pre, post]) => {
        // Filter out any objects without a text property
        const filteredPre = pre.filter((w: any) => typeof w.text === "string" && w.text.length > 0);
        const filteredPost = post.filter((w: any) => typeof w.text === "string" && w.text.length > 0);

        const preSet = new Set(filteredPre.map((w: Theme) => w.text.toLowerCase()));
        const postSet = new Set(filteredPost.map((w: Theme) => w.text.toLowerCase()));

        const sortedPre = filteredPre
          .map((w: Theme) => ({
            ...w,
            diffType: postSet.has(w.text.toLowerCase()) ? "common" : "unique-pre"
          }))
          .sort((a: Theme, b: Theme) => b.value - a.value)
          .slice(0, 15);

        const sortedPost = filteredPost
          .map((w: Theme) => ({
            ...w,
            diffType: preSet.has(w.text.toLowerCase()) ? "common" : "unique-post"
          }))
          .sort((a: Theme, b: Theme) => b.value - a.value)
          .slice(0, 15);

        // Build maps for quick lookup
        const preMap = new Map<string, Theme>(
          sortedPre.map((w: Theme) => [w.text.toLowerCase(), w])
        );
        const postMap = new Map<string, Theme>(
          sortedPost.map((w: Theme) => [w.text.toLowerCase(), w])
        );

        // Combine all unique themes from both sets
        const allThemes = Array.from(new Set([
          ...sortedPre.map((w: Theme) => w.text.toLowerCase()),
          ...sortedPost.map((w: Theme) => w.text.toLowerCase())
        ]));

        // For each theme, determine diffType and value
        const comparison = allThemes.map(text => {
          const preTheme = preMap.get(text);
          const postTheme = postMap.get(text);
          
          let diffType = "common";
          if (preTheme && !postTheme) diffType = "unique-pre";
          else if (!preTheme && postTheme) diffType = "unique-post";
          
          // Merge or select properties
          const merged = {
            text: preTheme?.text ?? postTheme?.text ?? "",
            value: Math.max(preTheme?.value ?? 0, postTheme?.value ?? 0),
            sentiment: preTheme?.sentiment ?? postTheme?.sentiment ?? 0,
            emotions: preTheme?.emotions ?? postTheme?.emotions ?? 
              { Happy: 0, Angry: 0, Surprise: 0, Sad: 0, Fear: 0 },
            keywords: preTheme?.keywords ?? postTheme?.keywords ?? [],
            related: preTheme?.related ?? postTheme?.related ?? [],
            diffType
          };
          
          return merged;
        }).sort((a, b) => b.value - a.value).slice(0, 30);

        setPreThemes(sortedPre);
        setPostThemes(sortedPost);
        setCompThemes(comparison);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading word cloud data:', error);
        setLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="loading-container" style={{
        display: 'flex',
        height: '500px',
        justifyContent: 'center',
        alignItems: 'center',
        color: '#3498db',
        fontFamily: 'Montserrat, sans-serif',
        fontSize: '20px',
      }}>
        <div>Generating visualization...</div>
      </div>
    );
  }

  return (
    <div style={{ 
      borderRadius: '12px',
      padding: '30px',
    }}>
      <h1 style={{
        fontFamily: 'Montserrat, sans-serif',
        fontWeight: 800,
        fontSize: 34,
        textAlign: 'center',
        color: '#34495e',
        marginBottom: 40,
      }}>Enhanced Theme Analysis</h1>
      
      {/* View mode selector */}
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        gap: '12px', 
        marginBottom: '24px' 
      }}>
        <button
          onClick={() => setViewMode('comparison')}
          style={{
            background: viewMode === 'comparison' ? '#3498db' : '#f0f0f0',
            color: viewMode === 'comparison' ? 'white' : '#333',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 500,
          }}
        >
          Comparison View
        </button>
        <button
          onClick={() => setViewMode('sentiment')}
          style={{
            background: viewMode === 'sentiment' ? '#3498db' : '#f0f0f0',
            color: viewMode === 'sentiment' ? 'white' : '#333',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 500,
          }}
        >
          Sentiment View
        </button>
      </div>
      
      <div style={{
        display: 'flex',
        flexDirection: 'row',
        gap: '30px',
        justifyContent: 'center',
        alignItems: 'flex-start',
        width: '100%',
        marginBottom: 50
      }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            fontSize: 24,
            color: '#3498db',
            marginBottom: 8,
            position: 'relative',
            display: 'inline-block',
            padding: '0 10px 8px',
            borderBottom: '3px solid #3498db',
            marginLeft: '6rem'
          }}>Pre-Show Themes</h2>
          <SingleWordCloud 
            themes={preThemes} 
            title="" 
            colorBySentiment={viewMode === 'sentiment'} 
          />
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <h2 style={{
            fontFamily: 'Montserrat, sans-serif',
            fontWeight: 700,
            fontSize: 24,
            color: '#e74c3c',
            marginBottom: 8,
            position: 'relative',
            display: 'inline-block',
            padding: '0 10px 8px',
            borderBottom: '3px solid #e74c3c',
            marginLeft: '6rem'
          }}>Post-Show Themes</h2>
          <SingleWordCloud 
            themes={postThemes} 
            title="" 
            colorBySentiment={viewMode === 'sentiment'} 
          />
        </div>
      </div>

      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        marginBottom: 24,
        padding: '24px',
        background: 'rgba(56, 57, 76, 0.05)',
        borderRadius: '12px'
      }}>
        <h2 style={{
          fontFamily: 'Montserrat, sans-serif',
          fontWeight: 700,
          fontSize: 24,
          color: '#27ae60',
          marginBottom: 16,
          position: 'relative',
          display: 'inline-block',
          padding: '0 10px 8px',
          borderBottom: '3px solid #27ae60',
          marginLeft: '6rem'
        }}>Theme Comparison</h2>
        
        <div style={{
          display: 'flex',
          flexDirection: 'row',
          gap: '40px',
          justifyContent: 'center',
          alignItems: 'flex-start',
          width: '100%'
        }}>
          <SingleWordCloud 
            themes={compThemes} 
            title="" 
            colorByDiffType={viewMode === 'comparison'} 
            colorBySentiment={viewMode === 'sentiment'} 
          />
        </div>
        
        {viewMode === 'comparison' ? (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            marginTop: 24,
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 15
          }}>
            <span style={{
              color: "#27ae60",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(39, 174, 96, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#27ae60', marginRight: 8 }}></div>
              Common Themes
            </span>
            <span style={{
              color: "#e74c3c",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(231, 76, 60, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e74c3c', marginRight: 8 }}></div>
              Pre-Show Only
            </span>
            <span style={{
              color: "#3498db",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(52, 152, 219, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#3498db', marginRight: 8 }}></div>
              Post-Show Only
            </span>
          </div>
        ) : (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '16px',
            marginTop: 24,
            fontFamily: 'Montserrat, sans-serif',
            fontSize: 15
          }}>
            <span style={{
              color: "#e74c3c",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(231, 76, 60, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#e74c3c', marginRight: 8 }}></div>
              Negative
            </span>
            <span style={{
              color: "#95a5a6",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(149, 165, 166, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#95a5a6', marginRight: 8 }}></div>
              Neutral
            </span>
            <span style={{
              color: "#2ecc71",
              fontWeight: 600,
              padding: '6px 14px',
              borderRadius: 20,
              background: 'rgba(46, 204, 113, 0.1)',
              display: 'flex',
              alignItems: 'center'
            }}>
              <div style={{ width: 8, height: 8, borderRadius: '50%', background: '#2ecc71', marginRight: 8 }}></div>
              Positive
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default EnhancedWordCloudVis;
